import { useState, useEffect, useRef } from "react";
import { DashboardLayout } from "../../Components/Layout/DashboardLayout";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import CustomButton from "../../Components/CustomButton";
import { useErrorBoundary } from "../../Hooks/useErrorBoundary";

const TERMS_ENDPOINT = "/admin/terms-condition";

const TermsAndCondition = () => {
  
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const toastId = useRef(null);
  const token = localStorage.getItem("login") || "";
  const apiUrl = process.env.REACT_APP_BASE_URL;

  const { error, handleError, clearError } = useErrorBoundary();

  if (error) {
    throw error.error;
  }

  const showToast = (type, message) => {
    if (toastId.current) toast.dismiss(toastId.current);

    toastId.current = toast[type](message, {
      autoClose: 2500,
      position: "top-right",
    });
  };

  const safeParse = async (res) => {
    const contentType = res.headers.get("content-type");

    if (!contentType) {
      throw new Error("No content-type received from server");
    }

    if (contentType.includes("application/json")) {
      return await res.json();
    }

    throw new Error(`Invalid response format: ${contentType}`);
  };

  useEffect(() => {
    const controller = new AbortController();

    const fetchTerms = async () => {
      setLoading(true);
      setFetchError(null);

      try {
        const res = await fetch(`${apiUrl}${TERMS_ENDPOINT}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          signal: controller.signal,
        });

        if (!res.ok) {
          const error = new Error(`HTTP ${res.status}: ${res.statusText}`);
          error.statusCode = res.status;
          throw error;
        }

        const data = await safeParse(res);

        if (data?.status) {
          setContent(data?.data?.content || "");
        } else {
          throw new Error(data?.message || "Failed to fetch terms");
        }

      } catch (err) {
        if (err.name === "AbortError") return;

        console.error("[Terms] Fetch Error:", err);

        setFetchError(err.message);
        showToast("error", "Failed to load Terms & Conditions");

        handleError(err, {
          component: "TermsAndCondition",
          action: "fetchTerms",
          context: "api_failure",
          endpoint: TERMS_ENDPOINT,
        });

      } finally {
        setLoading(false);
      }
    };

    fetchTerms();

    return () => controller.abort();
  }, [apiUrl, token, handleError]);

  const handleSubmit = async () => {
    if (submitting) return;

    if (!content?.trim()) {
      showToast("warning", "Content cannot be empty");
      return;
    }

    setSubmitting(true);

    try {
      const res = await fetch(`${apiUrl}${TERMS_ENDPOINT}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: content.trim() }),
      });

      const data = await safeParse(res);

      if (!res.ok) {
        throw new Error(data?.message || `Update failed (${res.status})`);
      }

      showToast("success", data?.message || "Terms updated successfully");

    } catch (err) {
      console.error("[Terms] Submit Error:", err);

      showToast("error", err.message || "Update failed");

      handleError(err, {
        component: "TermsAndCondition",
        action: "handleSubmit",
        context: "update_failure",
        contentLength: content.length,
      });

    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = () => {
    clearError();
    setFetchError(null);
    window.location.reload(); 
  };

  return (
    <DashboardLayout>
      <div className="dashCard">

        {/* HEADER */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="mainTitle mb-0">Terms & Conditions</h2>

          {fetchError && (
            <div className="alert alert-warning mb-0">
              <small>
                ⚠️ {fetchError}
                <button
                  className="btn btn-link btn-sm ms-2"
                  onClick={handleRetry}
                >
                  Retry
                </button>
              </small>
            </div>
          )}
        </div>

        {/* LOADER */}
        {loading && (
          <div className="text-center mb-3">
            <span className="spinner-border" />
          </div>
        )}

        {/* EDITOR */}
        <ReactQuill
          value={content}
          onChange={setContent}
          readOnly={loading || !!fetchError}
          placeholder="Enter terms and conditions..."
          modules={{
            toolbar: [
              [{ header: [1, 2, 3, 4, 5, false] }],
              ["bold", "italic", "underline", "strike"],
              ["link"],
              [{ list: "ordered" }, { list: "bullet" }],
              ["clean"],
            ],
          }}
        />

        {/* ACTIONS */}
        <div className="d-flex justify-content-end mt-3">
          <CustomButton
            variant="primaryButton"
            onClick={handleSubmit}
            disabled={submitting || loading || !!fetchError}
            text={submitting ? "Updating..." : "Update Terms"}
          />
        </div>

      </div>
    </DashboardLayout>
  );
};

export default TermsAndCondition;